import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
} from "react";
import { Check } from "lucide-react";
import type {
  ChatUiAnswer,
  ChatUiQuestionnaire,
} from "./hooks/useChatUiActions";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface ChatQuestionApprovalDrawerProps {
  questionnaire: ChatUiQuestionnaire | null;
  isSubmitting?: boolean;
  onSubmit: (answers: ChatUiAnswer[]) => void | Promise<void>;
  onCancel: () => void;
}

export function ChatQuestionApprovalDrawer({
  questionnaire,
  isSubmitting,
  onSubmit,
  onCancel,
}: ChatQuestionApprovalDrawerProps) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  useEffect(() => {
    const draft = loadQuestionDraft(questionnaire?.actionId);
    setIndex(draft?.index ?? 0);
    setAnswers(draft?.answers ?? {});
  }, [questionnaire?.actionId]);

  useEffect(() => {
    if (!questionnaire) return;
    saveQuestionDraft(questionnaire.actionId, { index, answers });
  }, [answers, index, questionnaire]);

  const question = questionnaire?.questions[index];
  const total = questionnaire?.questions.length ?? 0;
  const canGoBack = index > 0;
  const canGoNext = index < total - 1;
  const currentValue = question ? (answers[question.id] ?? "") : "";
  const isRequiredMissing = !!question?.required && !currentValue.trim();
  const compiledAnswers = useMemo(() => {
    if (!questionnaire) return [];
    return questionnaire.questions.map((item) => ({
      id: item.id,
      label: item.label,
      value: answers[item.id] ?? "",
    }));
  }, [answers, questionnaire]);
  const handleSubmit = useCallback(async () => {
    if (!questionnaire) return;
    const actionId = questionnaire.actionId;
    await onSubmit(compiledAnswers);
    clearQuestionDraft(actionId);
  }, [compiledAnswers, onSubmit, questionnaire]);
  const handleBack = useCallback(() => {
    if (!questionnaire || isSubmitting) return;
    if (canGoBack) {
      setIndex((value) => Math.max(0, value - 1));
      return;
    }
    clearQuestionDraft(questionnaire.actionId);
    onCancel();
  }, [canGoBack, isSubmitting, onCancel, questionnaire]);
  const handleAdvance = useCallback(() => {
    if (isRequiredMissing || isSubmitting) return;
    if (canGoNext) {
      setIndex((value) => Math.min(total - 1, value + 1));
      return;
    }
    void handleSubmit();
  }, [canGoNext, handleSubmit, isRequiredMissing, isSubmitting, total]);
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!event.metaKey) return;
      if (event.key === "Enter") {
        event.preventDefault();
        handleAdvance();
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        handleBack();
      }
    },
    [handleAdvance, handleBack],
  );

  return (
    <div
      className="fintheon-chat-approval-overlay"
      role="dialog"
      aria-label={questionnaire?.title ?? "Harper approval questions"}
      onKeyDown={handleKeyDown}
    >
      {questionnaire && question && (
        <>
          <div className="fintheon-chat-approval-question">
            <div className="fintheon-chat-approval-meta">
              <span>{question.label}</span>
              <span>
                <FintheonNumericText text={index + 1} />/
                <FintheonNumericText text={total} kind="count" />
              </span>
            </div>
            {questionnaire.summary ? (
              <p className="fintheon-chat-approval-summary">
                {questionnaire.summary}
              </p>
            ) : null}
            <p className="fintheon-chat-approval-copy">{question.question}</p>
            <textarea
              value={currentValue}
              onChange={(event) =>
                setAnswers((current) => ({
                  ...current,
                  [question.id]: event.target.value,
                }))
              }
              placeholder={question.placeholder ?? "Answer..."}
              className="fintheon-chat-approval-textarea"
            />
          </div>

          <div className="fintheon-chat-approval-actions">
            <button
              type="button"
              onClick={handleBack}
              disabled={isSubmitting}
              className="fintheon-chat-approval-back"
              aria-keyshortcuts="Meta+ArrowLeft"
            >
              Back
            </button>
            {canGoNext ? (
              <button
                type="button"
                onClick={handleAdvance}
                disabled={isRequiredMissing || isSubmitting}
                className="fintheon-chat-approval-next"
                aria-keyshortcuts="Meta+Enter"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isRequiredMissing || isSubmitting}
                className="fintheon-chat-approval-next"
                aria-keyshortcuts="Meta+Enter"
              >
                <Check size={12} />
                {isSubmitting ? "Saving" : "Submit"}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface QuestionDraft {
  index: number;
  answers: Record<string, string>;
}

function draftKey(actionId: string | undefined) {
  return actionId ? `fintheon.chat.questionDraft.${actionId}` : null;
}

function loadQuestionDraft(actionId: string | undefined): QuestionDraft | null {
  const key = draftKey(actionId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as QuestionDraft) : null;
  } catch {
    return null;
  }
}

function saveQuestionDraft(actionId: string, draft: QuestionDraft) {
  const key = draftKey(actionId);
  if (key) window.localStorage.setItem(key, JSON.stringify(draft));
}

function clearQuestionDraft(actionId: string) {
  const key = draftKey(actionId);
  if (key) window.localStorage.removeItem(key);
}
